import { IUser } from '@/types';
import { formatDate, getUserName, getUserRole } from '@/utils';
import { useMemo } from 'react';

/** Params for the current user feature */
interface IUseCurrentUserFeatureParams {
  currentUser: IUser | null;
}

/** Return values for the current user feature */
interface IUseCurrentUserFeatureReturn {
  /** Formatted creation date of the current user */
  currentUserDateCreated: string;
  /** Formatted last updated date of the current user */
  currentUserDateUpdated: string;
  /** Email of the current user, or empty string if not available */
  currentUserEmail: string;
  /** First name of the current user */
  currentUserFirstName: string;
  /** Full name of the current user */
  currentUserFullName: string;
  /** Image URL of the current user, if available */
  currentUserImageUrl?: string;
  /** Role of the current user */
  currentUserRole: string;
  /** Username of the current user */
  currentUserUsername: string;
  /** UUID of the current user */
  currentUserUuid: string;
}

/**
 * Encapsulates all non-visual business logic related to the current user.
 */
export const useCurrentUserFeature = ({
  currentUser,
}: IUseCurrentUserFeatureParams): IUseCurrentUserFeatureReturn => {
  // ================================
  // Memoized user details
  // ================================
  const result = useMemo(
    () => ({
      dateCreated: currentUser ? formatDate(currentUser.createdAt, 'fullDate') : '',
      dateUpdated: currentUser ? formatDate(currentUser.updatedAt, 'fullDate') : '',
      email: currentUser?.email ? currentUser.email : '',
      firstName: currentUser ? getUserName(currentUser, 'firstName') : '',
      fullName: currentUser ? getUserName(currentUser, 'fullName') : '',
      imageUrl: currentUser?.imageUrl ? currentUser.imageUrl : undefined,
      role: currentUser?.role ? (getUserRole(currentUser.role) as string) : '',
      username: currentUser?.username ? currentUser.username : '',
      uuid: currentUser?.uuid ? currentUser.uuid : '',
    }),
    [currentUser],
  );

  // ================================
  // Feature contract return
  // ================================
  return {
    currentUserDateCreated: result.dateCreated,
    currentUserDateUpdated: result.dateUpdated,
    currentUserEmail: result.email,
    currentUserFirstName: result.firstName,
    currentUserFullName: result.fullName,
    currentUserImageUrl: result.imageUrl,
    currentUserRole: result.role,
    currentUserUsername: result.username,
    currentUserUuid: result.uuid,
  };
};
